import { formatDate } from "@angular/common";
import { Injectable } from "@angular/core";
import * as XLSX from "xlsx";
import { Payment } from "../models/offer.model";

export interface PaymentScheduleExportColumn {
  key: string;
  type: "currency" | "date";
  header: (isPublicView: boolean) => string;
  value: (payment: Payment) => number | Date;
}

const CURRENCY_FORMAT = "$#,##0.00;($#,##0.00)";
const DATE_FORMAT = "m/d/yy";
const SHEET_NAME = "Payment Schedule";
const COLUMN_WIDTH = 16;

@Injectable({ providedIn: "root" })
export class PaymentScheduleExportService {
  export(
    payments: Payment[],
    columns: PaymentScheduleExportColumn[],
    isPublicView: boolean
  ): void {
    this.writeWorkbook(this.buildWorkbook(payments, columns, isPublicView), this.fileName());
  }

  fileName(): string {
    return `payment_schedule_${formatDate(new Date(), "yyyy-MM-dd", "en-US")}.xlsx`;
  }

  buildWorkbook(
    payments: Payment[],
    columns: PaymentScheduleExportColumn[],
    isPublicView: boolean
  ): XLSX.WorkBook {
    const header = columns.map((column) => column.header(isPublicView));
    const rows = (payments ?? []).map((payment) =>
      columns.map((column) => {
        const value = column.value(payment);
        return column.type === "currency" ? this.roundToCents(value as number) : value;
      })
    );

    const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows], { cellDates: true });
    this.applyCellFormats(worksheet, columns, rows.length);
    worksheet["!cols"] = columns.map(() => ({ wch: COLUMN_WIDTH }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, SHEET_NAME);
    return workbook;
  }

  protected writeWorkbook(workbook: XLSX.WorkBook, fileName: string): void {
    XLSX.writeFile(workbook, fileName);
  }

  private roundToCents(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private applyCellFormats(
    worksheet: XLSX.WorkSheet,
    columns: PaymentScheduleExportColumn[],
    rowCount: number
  ): void {
    for (let row = 1; row <= rowCount; row++) {
      columns.forEach((column, col) => {
        const cell: XLSX.CellObject = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
        if (!cell) {
          return;
        }
        cell.z = column.type === "date" ? DATE_FORMAT : CURRENCY_FORMAT;
      });
    }
  }
}
